import type {
  BaseApiBatchDeleteResponse,
  BaseApiBatchPostResponse,
  BaseApiListResponse,
  BaseApiResponse,
} from './types/api-request';
import type {
  AllConstraints,
  ApiKey,
  AssignedUserRole,
  Constraint,
  ContentObject,
  ContentObjectReference,
  ContentObjectVersion,
  ContentTypeDefinition,
  CtdConstraint,
  DeclineAdditionalUserDataResponse,
  DefaultSpace,
  Media,
  NewSpace,
  Plan,
  PlanHistory,
  SearchItem,
  Space,
  User,
  UserAnalyticsData,
  UserRole,
  Webhook,
  WebhookExecute,
  WebhookLog,
} from './types/api-response';

import {
  getAccountActivation,
  postLogin,
  postRegister,
  postRegisterAppSumo,
} from './auth-requests';
import {
  makeBatchJSONQuery,
  makeBodilessQuery,
  makeJSONQuery,
} from './base-request';

export { postLogin, postRegister, postRegisterAppSumo, getAccountActivation };

export const listContentTypes = makeBodilessQuery<
  BaseApiListResponse<ContentTypeDefinition>
>('v1/internal/contenttype');

export const getContentType = makeBodilessQuery<
  BaseApiResponse<ContentTypeDefinition>
>('v1/internal/contenttype/{{contentTypeName}}');

export const putContentType = makeJSONQuery<
  BaseApiResponse<ContentTypeDefinition>
>('v1/internal/contenttype/{{contentTypeName}}', 'PUT');

export const patchContentType = makeJSONQuery<
  BaseApiResponse<ContentTypeDefinition>
>('v1/internal/contenttype/{{contentTypeName}}', 'PATCH');

export const deleteContentType = makeBodilessQuery<BaseApiResponse<undefined>>(
  'v1/internal/contenttype/{{contentTypeName}}',
  'DELETE',
);
export const postContentType = makeJSONQuery<
  BaseApiResponse<ContentTypeDefinition>
>('v1/internal/contenttype', 'POST');

export const getContentTypes = makeBodilessQuery<
  BaseApiListResponse<ContentTypeDefinition>
>('v1/internal/contenttype');

export const getContentObjects = makeBodilessQuery<
  BaseApiListResponse<ContentObject>
>('v1/content/{{contentTypeName}}');
export const batchDeleteContentObjects = makeJSONQuery(
  'v1/content/{{contentTypeName}}/batch-delete',
);

export const postContentObject = makeJSONQuery<BaseApiResponse<ContentObject>>(
  'v1/content/{{contentTypeName}}',
);
export const getContentObject = makeBodilessQuery<
  BaseApiResponse<ContentObject>
>('v1/content/{{contentTypeName}}/{{id}}');

export const getContentObjectVersions = makeBodilessQuery<
  BaseApiListResponse<ContentObjectVersion>
>('v1/content/{{contentTypeName}}/{{id}}/version');

export const getContentObjectVersion = makeBodilessQuery<
  BaseApiResponse<ContentObject>
>('v1/content/{{contentTypeName}}/{{id}}/version/{{versionNumber}}');

export const putContentObject = makeJSONQuery<BaseApiResponse<ContentObject>>(
  'v1/content/{{contentTypeName}}/{{id}}',
  'PUT',
);
export const patchContentObject = makeJSONQuery<BaseApiResponse<ContentObject>>(
  'v1/content/{{contentTypeName}}/{{id}}',
  'PATCH',
);
export const duplicateContentObject = makeJSONQuery<
  BaseApiResponse<ContentObject>
>('v1/content/{{contentTypeName}}/{{id}}/duplicate');

export const batchPostContentObject =
  makeBatchJSONQuery<BaseApiBatchPostResponse>(
    'v1/content/{{contentTypeName}}/batch',
    'POST',
  );
export const batchPatchContentObject =
  makeBatchJSONQuery<BaseApiBatchPostResponse>(
    'v1/content/{{contentTypeName}}/batch',
    'PATCH',
  );
export const deleteContentObject = makeBodilessQuery<
  BaseApiResponse<undefined>
>('v1/content/{{contentTypeName}}/{{id}}', 'DELETE');

export const batchDeleteMedia = makeJSONQuery<BaseApiBatchDeleteResponse>(
  'v1/content/_media/batch-delete',
  'POST',
);
export const updateMedia = makeJSONQuery<BaseApiResponse<ContentObject>>(
  'v1/content/_media/{{mediaId}}',
  'PATCH',
);
export const batchUpdateMedia = makeJSONQuery<BaseApiBatchPostResponse>(
  'v1/content/_media/batch',
  'PATCH',
);

export const listUnsplashMedia =
  makeBodilessQuery<BaseApiListResponse<Media>>('unsplash');

export const saveUnsplashMedia = makeBodilessQuery<BaseApiResponse<Media>>(
  'unsplash/{{mediaId}}',
);

export const getConstraints = makeBodilessQuery<BaseApiResponse<Constraint>>(
  'constraints/{{name}}',
);

export const getCtdConstraints = makeBodilessQuery<
  BaseApiResponse<CtdConstraint>
>('constraints/{{name}}');

export const getAllConstraints =
  makeBodilessQuery<BaseApiResponse<AllConstraints>>('constraints/all');

export const getUsers = makeBodilessQuery<BaseApiListResponse<User>>('users');

export const getUser = makeBodilessQuery<BaseApiResponse<User>>('users/{{id}}');

export const putUser = makeJSONQuery<BaseApiResponse<undefined>>(
  'users/{{id}}',
  'PUT',
);

export const patchUser = makeJSONQuery<BaseApiResponse<User>>(
  'users/{{id}}',
  'PATCH',
);

export const putUserConfig = makeJSONQuery<BaseApiResponse<undefined>>(
  'users/{{id}}/config',
  'PUT',
);

export const postUser = makeJSONQuery<BaseApiResponse<UserAnalyticsData>>(
  'users?redirectUri={{redirectUri}}',
  'POST',
);

export const putUserChangeDefaultSpace = makeJSONQuery<
  BaseApiResponse<undefined>
>('users/change-default-space/{{id}}', 'PUT');

export const deleteUser = makeJSONQuery<BaseApiResponse<UserAnalyticsData>>(
  'users/{{id}}',
  'DELETE',
);

export const getAllUsers =
  makeBodilessQuery<BaseApiListResponse<User>>('users-data-preview');

export const exportAllUsers = makeBodilessQuery<Response>(
  'users-data-preview',
  'GET',
  undefined,
  {
    headers: {
      Accept: 'text/csv',
    },
  },
);

export const activateUser = makeBodilessQuery<BaseApiResponse<undefined>>(
  'users/activate-user/{{id}}',
  'PUT',
);

export const putAdditionalUserData = makeJSONQuery<BaseApiResponse<undefined>>(
  'users/{{id}}/information',
  'PUT',
);

export const declineAdditionalUserData = makeJSONQuery<
  BaseApiResponse<DeclineAdditionalUserDataResponse>
>('users/{{id}}/information-decline', 'POST');

export const resendActivationEmail = makeBodilessQuery<
  BaseApiResponse<undefined>
>('users/resend-activation-email/{{id}}', 'PUT');

export const getAllSpaces = makeBodilessQuery<BaseApiListResponse<Space>>(
  'spaces-data-preview',
);
export const exportAllSpaces = makeBodilessQuery<Response>(
  'spaces-data-preview',
  'GET',
  undefined,
  {
    headers: {
      Accept: 'text/csv',
    },
  },
);

export const postMedia = makeJSONQuery<BaseApiResponse<Media>>('media');

export const getSearchObject =
  makeBodilessQuery<BaseApiListResponse<SearchItem>>('v1/search');

export const deleteAccountRequest = makeJSONQuery<BaseApiResponse<undefined>>(
  'delete-account-request?redirectUri={{redirectUri}}',
  'POST',
);

export const deleteAccount = makeJSONQuery<BaseApiResponse<UserAnalyticsData>>(
  'delete-account',
  'POST',
);

export const getApiKey =
  makeBodilessQuery<BaseApiListResponse<ApiKey>>('api-key');

export const deleteApiKey = makeBodilessQuery<BaseApiResponse<undefined>>(
  'api-key/{{id}}',
  'DELETE',
);

export const regenerateApiKey = makeJSONQuery<BaseApiResponse<ApiKey>>(
  'api-key/{{id}}/regenerate',
  'PUT',
);

export const updateApiKey = makeJSONQuery<BaseApiResponse<ApiKey>>(
  'api-key/{{id}}',
  'PUT',
);

export const postApiKey = makeJSONQuery<BaseApiResponse<ApiKey>>(
  'api-key',
  'POST',
);

export const putWebhook = makeJSONQuery<BaseApiResponse<Webhook>>(
  'v1/content/_webhooks/{{id}}',
  'PUT',
);

export const patchWebhook = makeJSONQuery<BaseApiResponse<Webhook>>(
  'v1/content/_webhooks/{{id}}',
  'PATCH',
);

export const postWebhooks = makeJSONQuery<BaseApiResponse<Webhook>>(
  'v1/content/_webhooks',
  'POST',
);

export const deleteWebhooks = makeBodilessQuery<BaseApiResponse<undefined>>(
  'v1/content/_webhooks/{{id}}',
  'DELETE',
);

export const listWebhooks = makeBodilessQuery<BaseApiListResponse<Webhook>>(
  'v1/content/_webhooks',
  'GET',
);

export const getWebhooks = makeBodilessQuery<BaseApiResponse<Webhook>>(
  'v1/content/_webhooks/{{id}}',
);

export const executeWebhooks = makeJSONQuery<BaseApiResponse<WebhookExecute>>(
  'v1/content/_webhooks/{{id}}/execute',
  'POST',
);

export const versionWebhooks = makeBodilessQuery<
  BaseApiResponse<ContentObjectVersion>
>('v1/content/_webhooks/{{id}}/version');

export const logsWebhooks = makeBodilessQuery<BaseApiListResponse<WebhookLog>>(
  'v1/content/_webhooks/{{id}}/logs',
);

export const publishContentObject = makeBodilessQuery<
  BaseApiResponse<undefined>
>('v1/content/{{objectType}}/{{objectId}}/publish', 'POST');

export const unpublishContentObject = makeBodilessQuery<
  BaseApiResponse<undefined>
>('v1/content/{{objectType}}/{{objectId}}/unpublish', 'POST');

export const archiveContentObject = makeBodilessQuery<
  BaseApiResponse<undefined>
>('v1/content/{{objectType}}/{{objectId}}/archive', 'POST');

export const scheduleContentObject = makeJSONQuery<BaseApiResponse<undefined>>(
  'v1/content/{{objectType}}/{{objectId}}/schedule',
  'POST',
);

export const unscheduleContentObject = makeJSONQuery<
  BaseApiResponse<undefined>
>('v1/content/{{objectType}}/{{objectId}}/unschedule', 'POST');

export const batchPublishContentObjects = makeJSONQuery<
  BaseApiResponse<undefined>
>('v1/content/{{objectType}}/batch-publish', 'POST');

export const batchUnpublishContentObjects = makeJSONQuery<
  BaseApiResponse<undefined>
>('v1/content/{{objectType}}/batch-unpublish', 'POST');

export const batchArchiveContentObjects = makeJSONQuery<
  BaseApiResponse<undefined>
>('v1/content/{{objectType}}/batch-archive', 'POST');

export const getContentObjectReferences = makeBodilessQuery<
  BaseApiResponse<ContentObjectReference[]>
>('v1/content/{{objectType}}/{{objectId}}/references?hydrate=1', 'GET');

export const checkoutPlanProperty = makeJSONQuery<
  BaseApiResponse<{ url: string }>
>('order/checkout/property?space={{spaceId}}&redirectUri={{uri}}', 'POST');

export const checkoutPlan = makeJSONQuery<BaseApiResponse<undefined>>(
  'order/checkout?redirectUri={{uri}}',
  'POST',
);

export const getAllRolesAssigned = makeBodilessQuery<
  BaseApiListResponse<AssignedUserRole>
>('role/user/{{id}}/all');

export const getAllUserRoles =
  makeBodilessQuery<BaseApiListResponse<AssignedUserRole>>('role/all');

export const getUserRoles =
  makeBodilessQuery<BaseApiListResponse<UserRole>>('role');

export const getUserRole =
  makeBodilessQuery<BaseApiResponse<UserRole>>('role/{{id}}');

export const putUserRole = makeJSONQuery<BaseApiResponse<UserRole>>(
  'role/{{id}}',
  'PUT',
);

export const patchUserRole = makeJSONQuery<BaseApiResponse<UserRole>>(
  'role/{{id}}',
  'PATCH',
);

export const deleteUserRole = makeJSONQuery<BaseApiResponse<undefined>>(
  'role/{{id}}',
  'DELETE',
);

export const postUserRole = makeJSONQuery<BaseApiResponse<UserRole>>(
  'role',
  'POST',
);

export const getPublicPlans = makeBodilessQuery<BaseApiListResponse<Plan>>(
  'plans/public',
  'GET',
);

export const getPlans = makeBodilessQuery<BaseApiListResponse<Plan>>(
  'plans',
  'GET',
);

export const getPlan = makeBodilessQuery<BaseApiResponse<Plan>>(
  'plans/{{id}}',
  'GET',
);

export const postPlan = makeJSONQuery<BaseApiResponse<Plan>>('plans', 'POST');

export const putPlan = makeJSONQuery<BaseApiResponse<Plan>>(
  'plans/{{id}}',
  'PUT',
);

export const patchPlan = makeJSONQuery<BaseApiResponse<Plan>>(
  'plans/{{id}}',
  'PATCH',
);

export const deletePlan = makeJSONQuery<BaseApiResponse<undefined>>(
  'plans/{{id}}',
  'DELETE',
);

export const postOrder = makeJSONQuery<BaseApiResponse<undefined>>(
  'order/dashboard?user={{userId}}&redirectUri={{uri}}',
  'POST',
);

export const getSpace = makeBodilessQuery<BaseApiResponse<Space>>(
  'v1/spaces/{{id}}',
  'GET',
);
export const putSpace = makeJSONQuery<BaseApiResponse<Space>>(
  'v1/spaces/{{id}}',
  'PUT',
);
export const patchSpace = makeJSONQuery<BaseApiResponse<Space>>(
  'v1/spaces/{{id}}',
  'PATCH',
);
export const deleteSpace = makeJSONQuery<BaseApiResponse<undefined>>(
  'v1/spaces/{{id}}',
  'DELETE',
);

export const putSpaceHasPlan = makeJSONQuery<BaseApiResponse<Space>>(
  'v1/spaces/{{id}}/override-plan',
  'PUT',
);

export const getSpacesList = makeBodilessQuery<BaseApiListResponse<Space>>(
  'v1/spaces',
  'GET',
);

export const getUserSpaces = makeBodilessQuery<BaseApiListResponse<Space>>(
  'v1/spaces/user-spaces',
  'GET',
);
export const getUserDefaultSpace = makeBodilessQuery<
  BaseApiResponse<DefaultSpace>
>('v1/spaces/default-space', 'GET');

export const postSpace = makeJSONQuery<BaseApiResponse<NewSpace>>(
  'v1/spaces?redirectUri={{uri}}',
  'POST',
);
export const updateSpacePlan = makeJSONQuery<
  BaseApiResponse<{ status: string; url: string }>
>('v1/spaces/{{id}}/change-plan?redirectUri={{uri}}', 'PUT');

export const getSpacePlanHistory =
  makeBodilessQuery<BaseApiListResponse<PlanHistory>>('plans/space/{{id}}');

export const postSpacePurge = makeJSONQuery<BaseApiResponse<undefined>>(
  'v1/spaces/{{id}}/purge',
  'POST',
);

export const postSupportIssue = makeJSONQuery('support/request');

export const synchronizationExport = makeBodilessQuery(
  'synchronization/export/{{spaceId}}',
  'GET',
);

export const synchronizationImport = makeJSONQuery('synchronization/import');

export const synchronizationSynchronize = makeJSONQuery(
  'synchronization/synchronize/{{targetSpaceId}}',
);
